import { computed, type Ref } from "vue";
import type {
  HighlightItem,
  PremadeHighlightItem,
} from "@/features/experience-shared/highlights-v2/types/highlight-item";
import { uuid } from "@/utils/uuid";
import { replaceSlice } from "@/features/experience-shared/highlights-v2/composables/highlight-slice";
import { useIndexedList } from "@/features/experience-shared/highlights-v2/composables/useIndexedList";

/**
 * Headless, store-free engine for the PREMADE slice of the highlights list.
 *
 * Premade highlights are masterdata references: added in bulk from the "Add premade" modal,
 * deduped by `code`, reorderable, removable — but never renamed inline (their text comes
 * from masterdata). The generic slice mechanics (remove / move / reorder over the shared
 * `HighlightItem[]`) come from `useIndexedList`; this composable adds only the premade-
 * specific bits: bulk add with dedupe-by-code, and the `addedPremadeCodes` set.
 *
 * It is intentionally free of UI concerns: no `readonly`, no edit lock, no
 * validation, no display index — whether a control is offered is the component's business.
 */

/** A premade masterdata option, as passed down from the store boundary. */
export type PremadeOption = {
  code: string;
  name: string;
};

export function usePremadeList(items: Ref<HighlightItem[]>) {
  const {
    items: premadeItems,
    remove,
    move,
    reorder,
  } = useIndexedList<HighlightItem, PremadeHighlightItem>(
    items,
    (parent) => parent.filter((item): item is PremadeHighlightItem => item.kind === "premade"),
    (next) => replaceSlice(items.value, "premade", next)
  );

  /** Codes already present — the dedupe source for `addPremade`. */
  const addedPremadeCodes = computed<Set<string>>(() => new Set(premadeItems.value.map((item) => item.code)));

  /**
   * Adds premade options by code, skipping any already present and any repeated within the
   * same call (dedupe by code). Appends after the existing premade slice.
   */
  function addPremade(options: PremadeOption[]): void {
    const existing = addedPremadeCodes.value;
    const seen = new Set<string>();

    const additions: PremadeHighlightItem[] = [];
    for (const option of options) {
      if (existing.has(option.code) || seen.has(option.code)) {
        continue;
      }
      seen.add(option.code);
      additions.push({ kind: "premade", id: uuid(), name: option.name, code: option.code });
    }

    if (additions.length === 0) {
      return;
    }

    reorder([...premadeItems.value, ...additions]);
  }

  /**
   * Reconciles the premade slice against a full desired selection (dedupe by code). This is
   * the "modal is the source of truth" path: the `options` are the complete set of codes that
   * should be present after the call.
   *
   * - Codes already present are KEPT in their existing position with their existing item
   *   (id preserved, so drag order / adapter round-trips are untouched).
   * - Codes absent from `options` are REMOVED.
   * - New codes are APPENDED after the surviving slice, in the order they appear in `options`.
   */
  function syncPremade(options: PremadeOption[]): void {
    const desired = new Map<string, PremadeOption>();
    for (const option of options) {
      if (!desired.has(option.code)) {
        desired.set(option.code, option);
      }
    }

    // Keep existing items whose code is still desired, preserving their current order + id.
    const kept = premadeItems.value.filter((item) => desired.has(item.code));
    const keptCodes = new Set(kept.map((item) => item.code));

    // Append desired codes that are not already present, in the order they were selected.
    const additions: PremadeHighlightItem[] = [];
    for (const option of desired.values()) {
      if (keptCodes.has(option.code)) {
        continue;
      }
      additions.push({ kind: "premade", id: uuid(), name: option.name, code: option.code });
    }

    reorder([...kept, ...additions]);
  }

  return {
    premadeItems,
    addedPremadeCodes,
    addPremade,
    syncPremade,
    reorderPremade: reorder,
    removePremade: remove,
    movePremade: move,
  };
}
